import { Controller } from '@hotwired/stimulus';
import { openModal } from './modal_controller';

// Powers the analytics dashboard cards page: public-dashboard sharing (opens a
// modal whose body is a Turbo Frame), the client-side favourites filter,
// and dashboard duplicate/delete (Turbo forms). The favorite star itself is a
// plain Turbo Stream form (app/views/analytics/_favorite_star.html.haml).
export default class extends Controller {
  static targets = [
    'shareFrame',
    'duplicateSourceUid',
    'duplicateName',
    'duplicateNewUid',
    'duplicateFolder',
    'moveUid',
    'moveMessage',
    'moveFolder',
    'deleteUid',
    'deleteMessage',
    'permissionsUid',
    'permissionsMessage',
  ];

  static values = {
    shareLinkUrl: String,
    copySuffix: String,
  };

  // the action buttons sit inside the card's link; stop the click from bubbling to it
  // and navigating away. Not preventDefault: the favorite star is a real submit
  // button now and needs its own default (form submission) to go through.
  stopClick(event) {
    event.stopPropagation();
  }

  // ---- Public dashboard sharing ----
  // Load the current share state into the modal's Turbo Frame, then open the
  // modal. The generate/revoke buttons inside the frame are Rails forms that
  // submit via Turbo, so there's no fetch/JSON here.
  openShareModal(event) {
    this.shareFrameTarget.src = `${this.shareLinkUrlValue}?uid=${event.params.uid}`;
    openModal('publicDashboardModal');
  }

  // ---- Favourites ----

  // reads favorited state straight off each card's favorite-star button, which the
  // server keeps current via its own turbo_stream response - no separate state to sync.
  toggleFavoritesFilter(event) {
    const button = event.currentTarget;
    const container = button.closest("[data-controller~='filter']");
    if (!container) return;

    const controller = this.application.getControllerForElementAndIdentifier(container, 'filter');
    if (!controller) return;

    if (controller._favoritesEnabled === undefined) controller._favoritesEnabled = false;
    controller._favoritesEnabled = !controller._favoritesEnabled;

    button.classList.toggle('fill-tealLight', controller._favoritesEnabled);
    const svg = button.querySelector('svg');
    svg?.setAttribute('fill', controller._favoritesEnabled ? 'var(--teal-light)' : 'none');

    if (!controller._favoritesFilter) {
      controller._favoritesFilter = ({ el }) => {
        if (!controller._favoritesEnabled) return true;
        return el.querySelector('[data-favorited="true"]') !== null;
      };
      controller.addFilters(controller._favoritesFilter);
    }

    controller.filter();
  }

  // ---- Duplicate / Delete ----
  // These just prefill the modal form fields and open it; the modals are real
  // Rails forms that submit via Turbo, so there's no fetch/redirect logic here.

  openDuplicateModal(event) {
    this.duplicateSourceUidTarget.value = event.params.uid;
    this.duplicateNameTarget.value = `${event.params.title} ${this.copySuffixValue}`;
    this.duplicateNewUidTarget.value = '';
    // no folderUid param -> dashboard sits in the General folder (empty select value)
    this.duplicateFolderTarget.value = event.params.folderUid || '';
    openModal('duplicateDashboardModal');
  }

  openMoveModal(event) {
    this.moveUidTarget.value = event.params.uid;
    // no folderUid param -> dashboard sits in the General folder (empty select value)
    this.moveFolderTarget.value = event.params.folderUid || '';
    this.moveMessageTarget.textContent = this.moveMessageTarget.dataset.template.replace(
      '%{name}',
      event.params.title
    );
    openModal('moveDashboardModal');
  }

  openDeleteModal(event) {
    this.deleteUidTarget.value = event.params.uid;
    this.deleteMessageTarget.textContent = this.deleteMessageTarget.dataset.template.replace(
      '%{name}',
      event.params.title
    );
    openModal('deleteDashboardModal');
  }

  openPermissionsModal(event) {
    this.permissionsUidTarget.value = event.params.uid;
    this.permissionsMessageTarget.textContent =
      this.permissionsMessageTarget.dataset.template.replace('%{name}', event.params.title);
    openModal('permissionsDashboardModal');
  }
}
